import {
  useModalContext,
  WSButton,
  WSCheckboxToggle,
  WSElement,
  WSFlexGrid,
  WSForm,
  WSModal,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  TaxFilingType,
  Visibility
} from "@wingspanhq/users/dist/lib/interfaces";
import { CompanyStructure } from "@wingspanhq/users/dist/lib/interfaces/company";
import { AxiosError } from "axios";
import React from "react";
import * as Yup from "yup";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { US_STATES_OPTIONS_SHORT_NAME } from "../../../constants/user";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import {
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_USER_PROFILE
} from "../../../query/users/keys";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../../query/users/queries";
import { getPaymentsSetupProgress } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { usersService } from "../../../services/users";
import {
  AddressField,
  addressToValues,
  AddressValues,
  addressValuesToRequest
} from "../../../Settings/components/AddressField/AddressField";
import { BUSINESS_TYPE_OPTIONS } from "../../../Settings/components/SettingsUpdateForms/BusinessTypeForm";
import { WS_LINKS } from "../../../types/wsLinks";
import { getChangedData } from "../../../utils/getChangedData";
import { openIntercom } from "../../../utils/intercom";
import { openInNewTab } from "../../../utils/openInNewTab";
import {
  companyNameValidator,
  validatorAddress,
  validatorFirstName,
  validatorLastName,
  validatorTaxId
} from "../../../utils/validators";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import { useIsOnlyOneBusinessOwner } from "../../utils/setUp";

type InvoicesSetupStep1FormValues = {
  businessName: string;
  iHaveEin: boolean;
  legalBusinessName: string;
  ein: string;
  stateOfIncorporation: string;
  legalFirstName: string;
  legalLastName: string;
  businessType: CompanyStructure;
  isOnlyOneBusinessOwner: boolean;
  businessAddress: AddressValues;
};

export const SetupBusiness: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const activityQuery = useActivities(userId);
  const { openModal } = useModalContext();

  const [
    isOnlyOneBusinessOwner,
    setIsOnlyOneBusinessOwner
  ] = useIsOnlyOneBusinessOwner();

  const [submit, submitMeta] = useWSMutation<
    any,
    AxiosError,
    InvoicesSetupStep1FormValues
  >(
    async values => {
      const paymentsSetupProgress = getPaymentsSetupProgress(
        activityQuery.data
      );

      const isCompany = !!values.iHaveEin;

      if (values.legalFirstName || values.legalLastName) {
        await usersService.user.update(userId, {
          profile: getChangedData(userQuery.data?.profile, {
            firstName: values.legalFirstName,
            lastName: values.legalLastName
          })
        });
      }

      await usersService.member.update(userId, {
        memberId: userId,
        profile: getChangedData(memberQuery.data?.profile, {
          company: {
            name: values.businessName,
            taxId: isCompany ? values.ein : undefined,
            stateOfIncorporation: isCompany
              ? values.stateOfIncorporation
              : undefined,
            structure: isCompany ? values.businessType : undefined,
            legalBusinessName: isCompany ? values.legalBusinessName : undefined
          },
          taxInfo: {
            filingType: isCompany
              ? TaxFilingType.business
              : TaxFilingType.personal
          },
          address: addressValuesToRequest(values.businessAddress),
          visibilityMap: {
            phone: Visibility.Public,
            address: Visibility.Public
          }
        })
      });

      setIsOnlyOneBusinessOwner(
        isCompany ? values.isOnlyOneBusinessOwner : true
      );

      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            ...paymentsSetupProgress,
            currentStep: 2
          }
        }
      });
    },
    {
      dependencies: [
        QUERY_USERS_USER_PROFILE,
        QUERY_USERS_MEMBER_PROFILE,
        QUERY_USERS_ACTIVITIES
      ],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout
      onBack={onBack}
      title="Verify business information"
      description={
        <>
          Wingspan is required to collect this information for compliance
          purposes. Your information is{" "}
          <WSButton.Link
            inline
            onClick={() => {
              openInNewTab(WS_LINKS.security);
            }}
          >
            Certified Secure
          </WSButton.Link>
          .
          <br />
          <br />
          <WSButton.Link
            onClick={() => {
              openModal("WHY_DO_WE_COLLECT_INFORMATION");
            }}
          >
            Why do we collect this information
          </WSButton.Link>
        </>
      }
    >
      <WSModal
        name="WHY_DO_WE_COLLECT_INFORMATION"
        title="Why do we collect this information?"
        size="S"
      >
        <WSText mb="M">
          Wingspan collects your W-9 or W-8BEN to facilitate client’s tax
          reporting requirements.
        </WSText>
        <WSButton.Link
          mb="M"
          onClick={() => {
            openInNewTab(WS_LINKS.security);
          }}
        >
          Wingspan is SOC 2 and PCI certified secure.
        </WSButton.Link>
        <WSText>
          Have a question?{" "}
          <WSButton.Link inline onClick={openIntercom}>
            Contact support
          </WSButton.Link>
        </WSText>
      </WSModal>
      <WSQueries
        queries={{
          userQuery,
          memberQuery
        }}
      >
        {({ userQuery: { data: user }, memberQuery: { data: member } }) => (
          <WSForm<InvoicesSetupStep1FormValues>
            defaultValues={{
              businessName: member.profile.company?.name || "",
              iHaveEin: !!member.profile.company?.taxId,
              legalBusinessName: member.profile.company?.legalBusinessName,
              ein: member.profile.company?.taxId,
              legalFirstName: user.profile.firstName,
              legalLastName: user.profile.lastName,
              stateOfIncorporation:
                member.profile.company?.stateOfIncorporation,
              businessType: member.profile.company?.structure,
              isOnlyOneBusinessOwner:
                isOnlyOneBusinessOwner === null ||
                isOnlyOneBusinessOwner === undefined
                  ? true
                  : isOnlyOneBusinessOwner,
              businessAddress: member.profile.address
                ? addressToValues(member.profile.address)
                : {
                    country: "US",
                    addressLine1: "",
                    addressLine2: "",
                    city: "",
                    state: "",
                    zipCode: ""
                  }
            }}
            validationSchema={Yup.object().shape({
              businessName: companyNameValidator,
              iHaveEin: Yup.boolean(),
              legalBusinessName: Yup.string().when("iHaveEin", {
                is: true,
                then: companyNameValidator.required("Required")
              }),
              ein: Yup.string().when("iHaveEin", {
                is: true,
                then: validatorTaxId.required("Required")
              }),
              stateOfIncorporation: Yup.string().when("iHaveEin", {
                is: true,
                then: Yup.string().required("Required")
              }),
              businessType: Yup.string().when("iHaveEin", {
                is: true,
                then: Yup.string().required("Required")
              }),
              legalFirstName: Yup.string().when("iHaveEin", {
                is: true,
                then: Yup.string().when("businessType", {
                  is: businessType => {
                    return (
                      !businessType ||
                      businessType === CompanyStructure.LlcSingleMember ||
                      businessType === CompanyStructure.SoleProprietorship ||
                      businessType === CompanyStructure.None
                    );
                  },
                  then: validatorFirstName.required("Required")
                })
              }),
              legalLastName: Yup.string().when("iHaveEin", {
                is: true,
                then: Yup.string().when("businessType", {
                  is: businessType => {
                    return (
                      !businessType ||
                      businessType === CompanyStructure.LlcSingleMember ||
                      businessType === CompanyStructure.SoleProprietorship ||
                      businessType === CompanyStructure.None
                    );
                  },
                  then: validatorLastName.required("Required")
                })
              }),
              businessAddress: validatorAddress
            })}
            onSubmit={values => {
              submit(values);
            }}
          >
            <WSText.Heading3 mb="S">
              Business information (optional)
            </WSText.Heading3>
            <WSText.Label mb="M">
              You do not need to enter business information if you are not an
              LLC or Partnership.
            </WSText.Label>

            <WSForm.Field
              name="iHaveEin"
              component={WSCheckboxToggle}
              componentProps={{
                label: "I have an Employer Identification Number (EIN)"
              }}
              mb="M"
            />

            <WSForm.Value name="iHaveEin">
              {visible => (
                <WSElement hidden={!visible}>
                  <WSForm.Field
                    name="legalBusinessName"
                    label="Legal business name"
                    component={WSTextInput}
                    mb="M"
                  />

                  <WSForm.Field
                    name="businessType"
                    label="Business type"
                    component={WSSelect}
                    componentProps={{
                      options: BUSINESS_TYPE_OPTIONS
                    }}
                    mb="M"
                  />

                  <WSElement mb="M">
                    <WSFlexGrid container spacing="S">
                      <WSFlexGrid item mobile="6">
                        <WSForm.Field
                          name="ein"
                          label="EIN"
                          component={WSTextInput}
                          componentProps={{
                            formatOptions: {
                              numericOnly: true,
                              blocks: [2, 7],
                              delimiters: ["-"],
                              delimiterLazyShow: true
                            },
                            fsExclude: true
                          }}
                        />
                      </WSFlexGrid>
                      <WSFlexGrid item mobile="6">
                        <WSForm.Field
                          name="stateOfIncorporation"
                          component={WSSelect}
                          componentProps={{
                            options: US_STATES_OPTIONS_SHORT_NAME,
                            searchable: true
                          }}
                          label="State of incorporation"
                        />
                      </WSFlexGrid>
                    </WSFlexGrid>
                  </WSElement>

                  <WSForm.Value name="businessType">
                    {businessType => {
                      const singlePerson =
                        businessType === CompanyStructure.LlcSingleMember ||
                        businessType === CompanyStructure.SoleProprietorship ||
                        businessType === CompanyStructure.None;

                      return (
                        <>
                          <WSForm.Field
                            name="legalFirstName"
                            label="Legal first name"
                            component={WSTextInput}
                            hidden={!businessType || !singlePerson}
                            mb="M"
                          />
                          <WSForm.Field
                            name="legalLastName"
                            label="Legal last name"
                            component={WSTextInput}
                            hidden={!businessType || !singlePerson}
                            mb="M"
                          />
                          <WSForm.Field
                            hidden={!businessType || singlePerson}
                            name="isOnlyOneBusinessOwner"
                            component={WSCheckboxToggle}
                            componentProps={{
                              label: "I am the only owner of this business"
                            }}
                            mb="L"
                          />
                        </>
                      );
                    }}
                  </WSForm.Value>
                </WSElement>
              )}
            </WSForm.Value>

            <WSForm.Field
              name="businessName"
              label="Public business name  (optional)"
              description="This is the name that will appear to your contractors & clients."
              component={WSTextInput}
              mb="M"
            />

            <WSText.Heading3 mt="L" mb="M">
              Address
            </WSText.Heading3>

            <AddressField name="businessAddress" mb="M" />

            <WSErrorMessage
              mb="M"
              contextKey="PaymentsSetup"
              error={submitMeta.error}
            />

            <WSButton
              mt="L"
              fullWidth
              loading={submitMeta.isLoading}
              name="next"
            >
              Verify business information
            </WSButton>
          </WSForm>
        )}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};
